import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '@fdha/web-ui-library';
import {
  useGetMenuOptionSelectionsQuery,
  useGetSelectedMenuOptionsV2Query,
  useUpdatePatientMealSelectionV2Mutation,
  useGetDishQuery,
  useGetMealKitQuery,
  MenuOptionSelection,
  MealOptionType,
  DeliveryStatus,
  GetMenuOptionSelectionsQuery,
} from '@fdha/graphql-api-patient';
import { aggregateMealKitVariants } from '@fdha/common-utils';
import type {
  IPlatformBridge,
  ChooseMealsParams,
  ClosedDeliveryMenuOptions,
} from '@fdha/common-hooks';

class WebPlatformBridge implements IPlatformBridge {
  private navigate: ReturnType<typeof useNavigate>;
  private showSnackbar: ReturnType<typeof useSnackbar>['showSnackbarV2'];

  // Platform identification
  readonly isAdminPlatform = false;

  constructor(
    navigate: ReturnType<typeof useNavigate>,
    showSnackbar: ReturnType<typeof useSnackbar>['showSnackbarV2']
  ) {
    this.navigate = navigate;
    this.showSnackbar = showSnackbar;
  }

  // Navigation
  navigateToChooseMeals(params: ChooseMealsParams): void {
    // This would navigate to the Choose Meals screen when implemented
    // For now, we'll just go back as patient app doesn't have choose meals yet
    this.navigate(-1);
  }

  goBack(): void {
    this.navigate(-1);
  }

  // Notifications
  showSuccess(message: string): void {
    this.showSnackbar({ message, severity: 'success' });
  }

  showError(message: string): void {
    this.showSnackbar({ message, severity: 'error' });
  }

  // GraphQL hooks - direct assignment (interface uses Function types)
  useGetMenuOptionSelectionsQuery = useGetMenuOptionSelectionsQuery;
  useGetMenuOptionSelectionsV2Query = useGetMenuOptionSelectionsQuery; // Patient API uses V1 for both
  useGetSelectedMenuOptionsV2Query = useGetSelectedMenuOptionsV2Query;
  useUpdatePatientMealSelectionV2Mutation =
    useUpdatePatientMealSelectionV2Mutation;
  useGetDishQuery = useGetDishQuery;
  useGetMealKitQuery = useGetMealKitQuery;

  // GraphQL types
  MealSelections = null; // Patient API doesn't have this exact type
  MenuOptionSelection = {} as MenuOptionSelection;
  MealOptionType = MealOptionType;
  DeliveryStatus = DeliveryStatus;

  // Platform-specific utilities
  aggregateMealKitVariants = (items: MenuOptionSelection[]) => {
    // Use the utility function from common-utils, it handles the type conversion
    return aggregateMealKitVariants(items, 'web');
  };

  // Platform-specific query variable builder
  buildQueryVariables(
    patientId: string | null,
    dietPlanDeliveryMenuId: string | null
  ) {
    // Patient API only uses dietPlanDeliveryMenuId
    return {
      dietPlanDeliveryMenuId: dietPlanDeliveryMenuId || '',
    };
  }

  // Platform-specific response data extractor
  extractMenuOptionsData(data: GetMenuOptionSelectionsQuery) {
    // Patient API returns data.menuOptionSelectionV2
    return data?.menuOptionSelectionV2;
  }

  // Platform-specific closed delivery query variable builder
  buildClosedDeliveryQueryVariables(deliveryId: string) {
    return { deliveryId };
  }

  // Platform-specific closed delivery response data extractor
  extractClosedDeliveryData(data: { selectedMenuOptionsV2?: unknown }) {
    // Patient API returns data.selectedMenuOptionsV2
    return data?.selectedMenuOptionsV2 as ClosedDeliveryMenuOptions;
  }
}

// Hook to create the bridge instance
export const useWebPlatformBridge = (): IPlatformBridge => {
  const navigate = useNavigate();
  const { showSnackbarV2 } = useSnackbar();

  return new WebPlatformBridge(navigate, showSnackbarV2);
};
